import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Avatar, Card, SkeletonText } from '@/components/ui';
import { NavIcon } from '@/components/icons/NavIcon';
import { useRequests } from '@/hooks';
import type { RequestRecord, RequestType } from '@/api/types';
import { getErrorMessage } from '@/api';
import {
  buildGamesById,
  emailLocal,
  formatTimestamp,
  gameLabel,
  networkMetaKindLabel,
  requestTypeLabel,
  resellerAllowedKindLabel,
  stageGrantChanges,
  stageName,
} from '@/lib/format';
import { useMetaContext } from '@/components/Layout';
import { projectEditSummary } from './projectDetail/projectEditDiff';

import styles from './Inbox.module.css';

// plan §5.26 / §5.11: PageInbox → GET /api/requests?status=pending&type=
// 비Admin은 본인 요청만(mine=true).

type FilterKey =
  | 'all'
  | 'grant'
  | 'project_create'
  | 'project_edit'
  | 'price_apply'
  | 'api_token'
  | 'network_meta_apply'
  | 'reseller_allowed_apply'
  | 'role_member';
type StatusKey = 'pending' | 'approved' | 'rejected';

const FILTERS: { key: FilterKey; label: string; match: (t: RequestType) => boolean }[] = [
  { key: 'all', label: '전체', match: () => true },
  { key: 'grant', label: '권한', match: (t) => t === 'grant' || t === 'revoke' || t === 'stage_grant' },
  { key: 'project_create', label: '프로젝트 생성', match: (t) => t === 'project_create' },
  { key: 'project_edit', label: '프로젝트 수정', match: (t) => t === 'project_edit' },
  { key: 'price_apply', label: 'AF 단가', match: (t) => t === 'price_apply' },
  { key: 'api_token', label: 'API 토큰', match: (t) => t === 'api_token' },
  { key: 'network_meta_apply', label: '네트워크', match: (t) => t === 'network_meta_apply' },
  { key: 'reseller_allowed_apply', label: '리셀러 허용', match: (t) => t === 'reseller_allowed_apply' },
  { key: 'role_member', label: '가입', match: (t) => t === 'role_member' },
];

// 상태 필터 — 중복 선택 가능, 기본 '대기'만.
const STATUS_FILTERS: { key: StatusKey; label: string }[] = [
  { key: 'pending', label: '대기' },
  { key: 'approved', label: '승인' },
  { key: 'rejected', label: '반려' },
];

const STATUS_LABEL: Record<StatusKey, string> = {
  pending: '검토 대기',
  approved: '승인됨',
  rejected: '반려됨',
};

function summarizeGameScope(r: RequestRecord, gamesById: Record<string, string>): string {
  if (r.scope_type === 'all') return '모든 게임';
  return r.game_prefixes.map((g) => gameLabel(g, gamesById)).join(', ') || '—';
}

function summarize(r: RequestRecord, gamesById: Record<string, string>): string {
  if (r.request_type === 'grant') {
    return `${r.target_email ?? '—'} → ${r.level ?? '—'} · ${summarizeGameScope(r, gamesById)}`;
  }
  if (r.request_type === 'revoke') {
    return `${r.target_email ?? '—'} 회수 · ${summarizeGameScope(r, gamesById)}`;
  }
  if (r.request_type === 'project_create') {
    const p = r.payload as Record<string, unknown>;
    const name = (p?.display_name as string) ?? '';
    return name ? `${name}` : '신규 프로젝트';
  }
  if (r.request_type === 'project_edit') {
    // 키 이름 대신 실제 변경을 보인다 — 첫 변경은 '이전 → 이후', 나머지는 개수.
    return `${r.prefix ?? '—'} · ${projectEditSummary((r.payload ?? {}) as Record<string, unknown>)}`;
  }
  if (r.request_type === 'price_apply') {
    const operations = Array.isArray(r.payload?.operations) ? r.payload.operations : [];
    const deletes = operations.filter((operation: { type?: string; change?: { is_active?: boolean } }) => (
      operation?.type === 'update' && operation.change?.is_active === false
    )).length;
    return `AF CPE 단가 ${operations.length}개 변경${deletes > 0 ? ` · 삭제(off) ${deletes}개` : ''} · 관리자 승인 후 반영`;
  }
  if (r.request_type === 'api_token') {
    const p = r.payload as Record<string, unknown>;
    const name = (p?.name as string) ?? 'API 토큰';
    const days = p?.expires_in_days;
    return `${name} · ${days == null ? '무기한' : `${days}일`}`;
  }
  if (r.request_type === 'stage_grant') {
    const changes = stageGrantChanges(r.payload).map((c) => `${stageName(c.stage)} ${c.level ?? '회수'}`);
    return `${r.target_email ?? '—'} · ${changes.join(', ') || 'Stage 권한 변경'}`;
  }
  if (r.request_type === 'role_member') {
    return `${r.requester_email ?? '—'} · Orbit 첫 로그인 · 승인 시 member 부여`;
  }
  if (r.request_type === 'network_meta_apply') {
    // 요청 단위가 네트워크 1건이라 sc_naming 과 변경 종류만으로 충분히 구분된다.
    const p = r.payload as Record<string, unknown>;
    const scNaming = (p?.sc_naming as string) ?? '—';
    return `${scNaming} · ${networkMetaKindLabel(p?.kind as string)} · 관리자 승인 후 반영`;
  }
  if (r.request_type === 'reseller_allowed_apply') {
    // 요청 단위가 이메일 1건이다 — 대상 이메일과 작업 종류로 구분한다. 여러 이메일 추가는 첫 이메일 외 N개.
    const p = r.payload as Record<string, unknown>;
    const emails = Array.isArray(p?.reseller_emails) ? (p.reseller_emails as string[]) : [];
    const email = p?.kind === 'bulk_create' && emails.length > 0
      ? `${emails[0]}${emails.length > 1 ? ` 외 ${emails.length - 1}개` : ''}`
      : (p?.reseller_email as string) ?? '—';
    return `${email} · ${resellerAllowedKindLabel(p?.kind as string)} · 관리자 승인 후 반영`;
  }
  return r.reason ?? '';
}

function displayRequestTypeLabel(requestType: RequestType): string {
  return requestType === 'price_apply' ? 'AF CPE 단가 반영 요청' : requestTypeLabel(requestType);
}

export default function InboxPage() {
  const meta = useMetaContext();
  const isAdmin = meta.current_user.platform_role === 'admin';
  const [filter, setFilter] = useState<FilterKey>('all');
  // 상태 필터 — 중복 선택, 기본 '대기'만.
  const [statuses, setStatuses] = useState<Set<StatusKey>>(new Set(['pending']));

  const toggleStatus = (key: StatusKey) => {
    setStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // 상태(status)는 클라에서 필터하므로 백엔드에는 status 없이 요청 — 전체 상태를 받아온다.
  // admin은 전체 요청, 그 외는 본인 요청만 (백엔드 viewer_email 판정).
  const requests = useRequests({
    mine: isAdmin ? undefined : true,
  });

  // 게임 id/prefix → 게임명 맵 (meta.games). 요청 요약에서 게임 id를 이름과 함께 표시.
  const gamesById = useMemo(() => buildGamesById(meta.games), [meta.games]);

  const all = requests.data?.requests ?? [];
  const cfg = FILTERS.find((f) => f.key === filter) ?? FILTERS[0];
  const filtered = all.filter(
    (r) => cfg.match(r.request_type) && statuses.has(r.status as StatusKey),
  );

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>요청함</h1>
          <div className={styles.subtitle}>
            {isAdmin ? '전체 요청 ' : '내 요청 '}
            <span className={styles.strong}>{filtered.length}건</span>
          </div>
        </div>
      </div>

      <div className={styles.filterBar}>
        {/* 타입 필터 — 왼쪽 */}
        <div className={styles.filters}>
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`${styles.filter} ${filter === f.key ? styles.active : ''}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
        {/* 상태 필터 — 오른쪽, 중복 선택 */}
        <div className={styles.statusFilters}>
          {STATUS_FILTERS.map((s) => (
            <button
              key={s.key}
              type="button"
              className={`${styles.statusFilter} ${statuses.has(s.key) ? styles.statusActive : ''}`}
              onClick={() => toggleStatus(s.key)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* 요청 목록 로딩 — 텍스트 대신 스켈레톤. (규칙: orbit-loading-skeleton 스킬) */}
      {requests.loading && (
        <Card>
          <SkeletonText lines={4} lastWidth="45%" gap={12} lineHeight={14} />
        </Card>
      )}

      {requests.error && (
        <Card>
          <div className={styles.empty}>
            <div className={styles.emptyTitle}>요청 목록을 불러오지 못했어요</div>
            <div className={styles.emptyDesc}>{getErrorMessage(requests.error)}</div>
          </div>
        </Card>
      )}

      {!requests.loading && !requests.error && filtered.length === 0 && (
        <Card>
          <div className={styles.empty}>
            <div className={styles.emptyIcon}>
              <NavIcon name="check" size={24} color="#1F5A2C" />
            </div>
            <div className={styles.emptyTitle}>표시할 요청이 없어요</div>
            <div className={styles.emptyDesc}>
              선택한 상태에 해당하는 요청이 없어요. 오른쪽 상태 필터를 바꿔보세요.
            </div>
          </div>
        </Card>
      )}

      {!requests.loading && filtered.length > 0 && (
        <div className={styles.list}>
          {filtered.map((r) => (
            <Link key={r.id} to={`/inbox/${r.id}`} className={styles.row}>
              <div className={styles.typeIcon}>
                <NavIcon name="inbox" size={20} />
              </div>
              <div className={styles.titleCell}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                  <span className={styles.label}>{displayRequestTypeLabel(r.request_type)}</span>
                </div>
                <div className={styles.summary}>{summarize(r, gamesById)}</div>
              </div>
              <div className={styles.requesterCell}>
                <Avatar email={r.requester_email} name={emailLocal(r.requester_email)} size={28} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ font: '600 12.5px/1.2 "Pretendard"', whiteSpace: 'nowrap' }}>
                    {emailLocal(r.requester_email)}
                  </div>
                  <div style={{ font: '500 11px/1.2 "Pretendard"', color: 'var(--sc-text-muted)', whiteSpace: 'nowrap' }}>
                    {formatTimestamp(r.requested_at)}
                  </div>
                </div>
              </div>
              <span className={`${styles.statusChip} ${styles[r.status] ?? ''}`}>
                <span className={styles.statusDot} />
                {STATUS_LABEL[r.status as StatusKey] ?? r.status}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
